/** Spin button with idle, spinning, and disabled states — uses actual spin button assets */

import { Container, Sprite, Texture, Assets, Graphics, Text, TextStyle } from 'pixi.js';
import { Logger } from '../utils/Logger';

const logger = new Logger('SpinButton');

export enum SpinButtonState {
  IDLE = 'IDLE',
  SPINNING = 'SPINNING',
  DISABLED = 'DISABLED',
}

/** Asset paths for spin button states */
const SPIN_ASSETS = {
  normal: 'assets/ui/spin_button_normal.png',
  pressed: 'assets/ui/spin_button_pressed.png',
  disabled: 'assets/ui/spin_button_disabled.png',
  iconNormal: 'assets/ui/spin_icon_normal.png',
  iconPressed: 'assets/ui/spin_icon_pressed.png',
  iconDisabled: 'assets/ui/spin_icon_disabled.png',
  stopNormal: 'assets/ui/stop_button_normal.png',
  stopPressed: 'assets/ui/stop_button_pressed.png',
};

/** Interactive spin button with layered background + icon sprites */
export class SpinButton extends Container {
  private _state: SpinButtonState = SpinButtonState.IDLE;
  private _autoplayRemaining: number = 0;
  private _turboMode: boolean = false;
  private _onSpin: (() => void) | null = null;

  // Textures (loaded async)
  private _texBgNormal: Texture | null = null;
  private _texBgPressed: Texture | null = null;
  private _texBgDisabled: Texture | null = null;
  private _texIconNormal: Texture | null = null;
  private _texIconPressed: Texture | null = null;
  private _texIconDisabled: Texture | null = null;
  private _texStopNormal: Texture | null = null;
  private _texStopPressed: Texture | null = null;

  // Sprites (two layers)
  private _bgSprite: Sprite | null = null;
  private _iconSprite: Sprite | null = null;

  // Fallback graphics
  private _fallbackBg: Graphics | null = null;
  private _fallbackText: Text | null = null;
  private _useFallback: boolean = true;

  constructor() {
    super();
    this.eventMode = 'static';
    this.cursor = 'pointer';

    // Create fallback graphics immediately so the button is visible
    this._createFallback();

    // Load real assets asynchronously
    this._loadAssets();

    // Pointer events
    this.on('pointerdown', this._handlePointerDown, this);
    this.on('pointerup', this._handlePointerUp, this);
    this.on('pointerupoutside', this._handlePointerUp, this);

    // Keyboard: Space bar triggers spin
    this._setupKeyboard();
  }

  get buttonState(): SpinButtonState {
    return this._state;
  }

  get isTurbo(): boolean {
    return this._turboMode;
  }

  get autoplayRemaining(): number {
    return this._autoplayRemaining;
  }

  /** Set the callback for spin press */
  onSpin(callback: () => void): void {
    this._onSpin = callback;
  }

  /** Set the visual state */
  setState(state: SpinButtonState): void {
    this._state = state;
    this._updateVisuals();
    this.cursor = state === SpinButtonState.IDLE ? 'pointer' : 'default';
  }

  /** Start autoplay with N spins (-1 for unlimited) */
  startAutoplay(count: number): void {
    this._autoplayRemaining = count;
    logger.info(`Autoplay started: ${count === -1 ? 'unlimited' : count} spins`);
  }

  /** Decrement autoplay count. Returns true if autoplay should continue */
  tickAutoplay(): boolean {
    if (this._autoplayRemaining === 0) return false;
    if (this._autoplayRemaining === -1) return true; // unlimited
    this._autoplayRemaining--;
    logger.debug(`Autoplay remaining: ${this._autoplayRemaining}`);
    return this._autoplayRemaining > 0;
  }

  /** Stop autoplay */
  stopAutoplay(): void {
    this._autoplayRemaining = 0;
    logger.info('Autoplay stopped');
  }

  get isAutoplay(): boolean {
    return this._autoplayRemaining !== 0;
  }

  /** Toggle turbo mode */
  toggleTurbo(): void {
    this._turboMode = !this._turboMode;
    logger.info(`Turbo mode: ${this._turboMode}`);
  }

  private _createFallback(): void {
    this._useFallback = true;

    const bg = new Graphics();
    bg.circle(0, 0, 55);
    bg.fill({ color: 0x22aa44 });
    bg.stroke({ color: 0xffffff, width: 3 });
    this._fallbackBg = bg;
    this.addChild(bg);

    const label = new Text({
      text: 'SPIN',
      style: new TextStyle({
        fontFamily: 'Arial Black, Arial',
        fontSize: 22,
        fill: 0xffffff,
        fontWeight: 'bold',
      }),
    });
    label.anchor.set(0.5);
    this._fallbackText = label;
    this.addChild(label);
  }

  private async _loadAssets(): Promise<void> {
    try {
      const [bgNorm, bgPress, bgDis, icNorm, icPress, icDis, stopNorm, stopPress] =
        await Promise.all([
          Assets.load<Texture>(SPIN_ASSETS.normal).catch(() => null),
          Assets.load<Texture>(SPIN_ASSETS.pressed).catch(() => null),
          Assets.load<Texture>(SPIN_ASSETS.disabled).catch(() => null),
          Assets.load<Texture>(SPIN_ASSETS.iconNormal).catch(() => null),
          Assets.load<Texture>(SPIN_ASSETS.iconPressed).catch(() => null),
          Assets.load<Texture>(SPIN_ASSETS.iconDisabled).catch(() => null),
          Assets.load<Texture>(SPIN_ASSETS.stopNormal).catch(() => null),
          Assets.load<Texture>(SPIN_ASSETS.stopPressed).catch(() => null),
        ]);

      if (bgNorm) {
        this._texBgNormal = bgNorm;
        this._texBgPressed = bgPress;
        this._texBgDisabled = bgDis;
        this._texIconNormal = icNorm;
        this._texIconPressed = icPress;
        this._texIconDisabled = icDis;
        this._texStopNormal = stopNorm;
        this._texStopPressed = stopPress;

        // Create the two-layer sprites
        this._bgSprite = new Sprite(bgNorm);
        this._bgSprite.anchor.set(0.5);
        this.addChild(this._bgSprite);

        this._iconSprite = new Sprite(icNorm || bgNorm);
        this._iconSprite.anchor.set(0.5);
        this.addChild(this._iconSprite);

        // Remove fallback
        if (this._fallbackBg) {
          this.removeChild(this._fallbackBg);
          this._fallbackBg.destroy();
          this._fallbackBg = null;
        }
        if (this._fallbackText) {
          this.removeChild(this._fallbackText);
          this._fallbackText.destroy();
          this._fallbackText = null;
        }
        this._useFallback = false;

        logger.info('Spin button assets loaded (layered bg + icon)');
        this._updateVisuals();
      } else {
        logger.warn('Spin button normal texture not found, using fallback');
      }
    } catch (err) {
      logger.warn(`Failed to load spin button assets: ${err}`);
    }
  }

  private _updateVisuals(): void {
    if (this._useFallback) {
      this._updateFallback();
      return;
    }

    const isIdle = this._state === SpinButtonState.IDLE;
    const isSpinning = this._state === SpinButtonState.SPINNING;
    const isDisabled = this._state === SpinButtonState.DISABLED;

    if (this._bgSprite) {
      if (isIdle) {
        this._bgSprite.texture = this._texBgNormal!;
        this._bgSprite.visible = true;
      } else if (isSpinning) {
        this._bgSprite.texture = this._texStopNormal || this._texBgNormal!;
        this._bgSprite.visible = true;
      } else if (isDisabled) {
        this._bgSprite.texture = this._texBgDisabled || this._texBgNormal!;
        this._bgSprite.visible = true;
      }
    }

    if (this._iconSprite) {
      if (isIdle) {
        this._iconSprite.texture = this._texIconNormal!;
        this._iconSprite.visible = !!this._texIconNormal;
      } else if (isSpinning) {
        // Stop mode — hide icon overlay (stop button is a single image)
        this._iconSprite.visible = false;
      } else if (isDisabled) {
        this._iconSprite.texture = this._texIconDisabled || this._texIconNormal!;
        this._iconSprite.visible = !!this._texIconDisabled || !!this._texIconNormal;
      }
    }
  }

  private _updateFallback(): void {
    if (!this._fallbackBg || !this._fallbackText) return;

    switch (this._state) {
      case SpinButtonState.IDLE:
        this._fallbackBg.clear();
        this._fallbackBg.circle(0, 0, 55);
        this._fallbackBg.fill({ color: 0x22aa44 });
        this._fallbackBg.stroke({ color: 0xffffff, width: 3 });
        this._fallbackText.text = 'SPIN';
        this.alpha = 1;
        break;
      case SpinButtonState.SPINNING:
        this._fallbackBg.clear();
        this._fallbackBg.circle(0, 0, 55);
        this._fallbackBg.fill({ color: 0xcc3333 });
        this._fallbackBg.stroke({ color: 0xffffff, width: 3 });
        this._fallbackText.text = 'STOP';
        this.alpha = 1;
        break;
      case SpinButtonState.DISABLED:
        this._fallbackBg.clear();
        this._fallbackBg.circle(0, 0, 55);
        this._fallbackBg.fill({ color: 0x555555 });
        this._fallbackBg.stroke({ color: 0x888888, width: 3 });
        this._fallbackText.text = 'SPIN';
        this.alpha = 0.5;
        break;
    }
  }

  private _handlePointerDown(): void {
    if (this._state === SpinButtonState.IDLE) {
      // Show pressed state
      if (!this._useFallback && this._bgSprite && this._texBgPressed) {
        this._bgSprite.texture = this._texBgPressed;
      }
      if (!this._useFallback && this._iconSprite && this._texIconPressed) {
        this._iconSprite.texture = this._texIconPressed;
      }
      // Subtle haptic feedback for touch devices
      if (navigator.vibrate) {
        navigator.vibrate(10);
      }
      if (this._onSpin) {
        this._onSpin();
      }
    } else if (this._state === SpinButtonState.SPINNING) {
      // Show stop pressed state
      if (!this._useFallback && this._bgSprite && this._texStopPressed) {
        this._bgSprite.texture = this._texStopPressed;
      }
    }
  }

  /** Programmatically trigger spin (used by tap-to-spin on reels area) */
  triggerSpin(): void {
    if (this._state === SpinButtonState.IDLE && this._onSpin) {
      if (navigator.vibrate) {
        navigator.vibrate(10);
      }
      this._onSpin();
    }
  }

  private _handlePointerUp(): void {
    this._updateVisuals();
  }

  private _setupKeyboard(): void {
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.code === 'Space' || e.key === ' ') {
        e.preventDefault();
        if (this._state === SpinButtonState.IDLE && this._onSpin) {
          this._onSpin();
        }
      }
    });
  }
}
